"use client"

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { endOfDay, endOfMonth, format, startOfDay } from 'date-fns'
import { es } from 'date-fns/locale'
import { DateRange } from 'react-day-picker'
import {
  Calendar as CalendarIcon,
  DollarSign,
  FileJson,
  Loader2,
  Package,
  Percent,
  RefreshCw,
  Settings,
  ShoppingBag,
  TrendingDown,
  TrendingUp,
  Trash2,
} from 'lucide-react'

import { useSalesReport } from '@/hooks/useSalesReport'
import { useToast } from '@/hooks/useToast'
import { FunctionsService } from '@/services/functions.service'
import type { ProductSale } from '@/lib/types'
import { cn, formatCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Header } from '../layout/Header'

const DEFAULT_RANGE: DateRange = {
  from: startOfDay(new Date()),
  to: endOfDay(new Date()),
}

type SortOption = 'most-sold' | 'less-sold' | 'highest-inventory' | 'lowest-inventory' | 'least-rotation' | 'longest-in-inventory' | 'longest-without-sales'


interface SalesConfig {
  sort: SortOption
}



const sortOptionLabels: Record<SortOption, string> = {
  'most-sold': 'Más vendidos (unidades)',
  'less-sold': 'Menos vendidos (unidades)',
  'highest-inventory': 'Mayor inventario',
  'lowest-inventory': 'Menor inventario',
  'least-rotation': 'Menor rotación (ventas/inv)',
  'longest-in-inventory': 'Más días en inventario',
  'longest-without-sales': 'Más días sin venderse',
}

const formatPercent = (value: number) =>
  `${new Intl.NumberFormat('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)}%`

const parseMoney = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/\s/g, '').replace(',', '.')
    const parsed = Number(normalized)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

const calculateDaysInInventory = (createdAt?: string | null): number | undefined => {
  if (!createdAt) return undefined
  const created = new Date(createdAt)
  if (isNaN(created.getTime())) return undefined
  const now = new Date()
  const diffMs = now.getTime() - created.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

const calculateDaysSinceLastSale = (lastSoldAt?: string | null): number | undefined => {
  if (!lastSoldAt) return undefined
  const lastSold = new Date(lastSoldAt)
  if (isNaN(lastSold.getTime())) return undefined
  const now = new Date()
  const diffMs = now.getTime() - lastSold.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

export function ProductSalesDashboard() {
  const { toast } = useToast()

  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE)
  const { report, loading, error, getSalesReport } = useSalesReport()
  const [config, setConfig] = useState<SalesConfig>({ sort: 'most-sold' })
  const [forceShopify, setForceShopify] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<'today' | 'month-to-date' | 'full-month' | 'custom'>('today')
  const [isDebugOpen, setIsDebugOpen] = useState(false)
  const [productImages, setProductImages] = useState<
    Record<
      string,
      { imageUrl?: string | null; handle?: string | null; title?: string | null; inventory?: number | null; createdAt?: string | null }
    >
  >({})
  const [allProducts, setAllProducts] = useState<{ id: string; title: string; inventory: number; createdAt?: string | null; handle?: string | null; imageUrl?: string | null; status?: string; vendor?: string; product_type?: string }[]>([])
  const [previewProduct, setPreviewProduct] = useState<{ src: string; title: string } | null>(null)
  const navigate = useNavigate()

  // Filtros
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'archived'>('all')
  const [inventoryFilter, setInventoryFilter] = useState<'all' | 'with-inventory' | 'zero-inventory'>('all')
  const [brandFilter, setBrandFilter] = useState<string>('all')

  const handleGenerateReport = useCallback(
    async (range: DateRange | undefined) => {
      if (!range?.from || !range?.to) return
      await getSalesReport(startOfDay(range.from), endOfDay(range.to), forceShopify)
    },
    [getSalesReport, forceShopify]
  )

  useEffect(() => {
    handleGenerateReport(dateRange)
    // Usar getProductsFromFirestore en lugar de getAllProducts para aprovechar el caché
    FunctionsService.getProductsFromFirestore().then((response) => {
      if (response.data) {
        setAllProducts(response.data.products)
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handlePresetChange = useCallback(
    (preset: 'today' | 'month-to-date' | 'full-month') => {
      const now = new Date()
      let nextRange: DateRange

      if (preset === 'today') {
        nextRange = { from: startOfDay(now), to: endOfDay(now) }
      } else if (preset === 'month-to-date') {
        const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
        nextRange = { from: monthStart, to: endOfDay(now) }
      } else {
        const monthStart = startOfDay(new Date(now.getFullYear(), now.getMonth(), 1))
        nextRange = { from: monthStart, to: endOfDay(endOfMonth(now)) }
      }

      setSelectedPreset(preset)
      setDateRange(nextRange)
      handleGenerateReport(nextRange)
    },
    [handleGenerateReport]
  )

  const handleDateChange = useCallback((range: DateRange | undefined) => {
    if (!range?.from) return

    const next: DateRange = {
      from: startOfDay(range.from),
      to: range.to ? endOfDay(range.to) : endOfDay(range.from),
    }

    setSelectedPreset('custom')
    setDateRange(next)
  }, [])

  const handleGenerateClick = useCallback(() => {
    handleGenerateReport(dateRange)
  }, [dateRange, handleGenerateReport])

  // Función para recargar productos desde Firestore
  const loadProductsFromCache = useCallback(async () => {
    const response = await FunctionsService.getProductsFromFirestore()
    if (response.data) {
      setAllProducts(response.data.products)
    }
  }, [])

  const handleClearCache = useCallback(async () => {
    try {
      // Limpiar caché de órdenes
      const response = await FunctionsService.clearCache(
        dateRange?.from && dateRange?.to ? { from: dateRange.from, to: dateRange.to } : undefined
      )

      if (response.error) {
        toast({
          title: 'Error al limpiar caché',
          description: response.error,
          variant: 'destructive',
        })
        return
      }

      // Sincronizar productos desde Shopify
      toast({
        title: 'Sincronizando productos...',
        description: 'Actualizando inventario desde Shopify. Esto puede tardar unos minutos.',
      })

      const syncResponse = await FunctionsService.syncProductsToFirestore()

      if (syncResponse.error) {
        toast({
          title: 'Caché limpiado pero error en sincronización',
          description: syncResponse.error,
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Sincronización completa',
          description: `${syncResponse.data?.totalProducts ?? 0} productos actualizados. La próxima consulta utilizará información actualizada.`,
        })
        // Recargar productos después de sincronizar
        await loadProductsFromCache()
      }
    } catch (error: any) {
      toast({
        title: 'Error al limpiar caché',
        description: error?.message ?? 'No se pudo limpiar la caché.',
        variant: 'destructive',
      })
    }
  }, [dateRange, toast, loadProductsFromCache])

  useEffect(() => {
    if (dateRange?.from && dateRange?.to) {
      handleGenerateReport(dateRange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceShopify])

  const productsFromReport = useMemo((): (ProductSale & { productIds?: string[] })[] => {
    if (report?.products?.length) {
      return report.products.map((product) => ({
        ...product,
        productName: product.productName,
        unitsSold: product.unitsSold ?? 0,
        totalSales: product.totalSales ?? 0,
      }))
    }

    if (!report?.orders || report.orders.length === 0) {
      // Si no hay órdenes, devolver productos del inventario con ventas en 0
      return allProducts.map((product) => ({
        productName: product.title,
        unitsSold: 0,
        totalSales: 0,
        inventory: Number(product.inventory ?? 0),
        productIds: [String(product.id)],
        createdAt: product.createdAt ?? undefined,
      }))
    }

    const aggregate = new Map<
      string,
      {
        name: string
        units: number
        sales: number
        imageUrl?: string
        productIds: Set<string>
        lastSoldAt?: string | null
      }
    >()

    report.orders.forEach((order) => {
      const items = (order as any).products ?? order.line_items ?? []
      items.forEach((item: any) => {
        const name: string = item.productName ?? item.name ?? 'Producto sin nombre'
        const key = name.toLowerCase()
        const quantityRaw = Number(item.units ?? item.quantity ?? 0)
        const quantity = Number.isFinite(quantityRaw) ? quantityRaw : 0

        const totalSalesRaw = parseMoney(item.totalSales)
        const priceRaw = parseMoney(item.price)
        const totalSales = totalSalesRaw > 0 ? totalSalesRaw : priceRaw * quantity

        const productIdCandidate =
          item.productId ?? item.product_id ?? item.id ?? item.productID ?? item.variant_id
        const productId =
          productIdCandidate !== null && productIdCandidate !== undefined
            ? String(productIdCandidate)
            : undefined

        const imageUrl: string | undefined =
          item.imageUrl ?? item.image ?? item.image_url ?? item.thumbnail ?? undefined

        const current = aggregate.get(key)
        const productIdsSet = current ? new Set(current.productIds) : new Set<string>()
        if (productId !== undefined) {
          productIdsSet.add(productId)
        }

        const orderDateRaw =
          (order as any).date ??
          (order as any).created_at ??
          (order as any).createdAt ??
          (order as any).orderDate ??
          null
        const orderTimestamp = orderDateRaw ? Date.parse(orderDateRaw) : Number.NaN

        const currentLastSoldTimestamp = current?.lastSoldAt ? Date.parse(current.lastSoldAt) : Number.NaN
        let updatedLastSoldAt = current?.lastSoldAt ?? null
        if (!Number.isNaN(orderTimestamp)) {
          if (Number.isNaN(currentLastSoldTimestamp) || orderTimestamp > currentLastSoldTimestamp) {
            updatedLastSoldAt = new Date(orderTimestamp).toISOString()
          }
        }

        aggregate.set(key, {
          name,
          units: (current?.units ?? 0) + quantity,
          sales: (current?.sales ?? 0) + totalSales,
          imageUrl: current?.imageUrl ?? imageUrl,
          productIds: productIdsSet,
          lastSoldAt: updatedLastSoldAt,
        })
      })
    })

    return Array.from(aggregate.values()).map((value) => ({
      productName: value.name,
      unitsSold: value.units,
      totalSales: value.sales,
      imageUrl: value.imageUrl,
      productIds: Array.from(value.productIds),
      lastSoldAt: value.lastSoldAt ?? undefined,
      inventory: 0, // Agregado para que coincida con el tipo ProductSale
    }))
  }, [report?.orders, report?.products, allProducts])

  const inventoryById = useMemo(() => {
    const map = new Map<string, number>()

    allProducts.forEach((product) => {
      const numericInventory = Number(product.inventory)
      map.set(String(product.id), Number.isFinite(numericInventory) ? numericInventory : 0)
    })

    Object.entries(productImages).forEach(([id, info]) => {
      const numericInventory = Number(info?.inventory)
      if (Number.isFinite(numericInventory)) {
        map.set(id, numericInventory)
      }
    })

    return map
  }, [allProducts, productImages])

  const createdAtById = useMemo(() => {
    const map = new Map<string, string>()

    allProducts.forEach((product) => {
      if (product.createdAt) {
        map.set(String(product.id), product.createdAt)
      }
    })

    Object.entries(productImages).forEach(([id, info]) => {
      if (info?.createdAt) {
        map.set(id, info.createdAt)
      }
    })

    return map
  }, [allProducts, productImages])

  // Unique vendors from allProducts catalog (vendor field from Shopify)
  const brands = useMemo(() => {
    const set = new Set<string>()
    allProducts.forEach((p) => {
      const v = p.vendor?.trim()
      if (v) set.add(v)
    })
    return Array.from(set).sort()
  }, [allProducts])

  // Unique product types (lines) from allProducts
  const productTypes = useMemo(() => {
    const set = new Set<string>()
    allProducts.forEach((p) => {
      const t = p.product_type?.trim()
      if (t) set.add(t)
    })
    return Array.from(set).sort()
  }, [allProducts])

  const [lineFilter, setLineFilter] = useState<string>('all')

  const productsWithInventory = useMemo(() => {
    return productsFromReport.map((product) => {
      const ids = Array.isArray(product.productIds) ? product.productIds : []
      const inventoryFromIds = ids.length
        ? ids.reduce((sum, id) => sum + (inventoryById.get(id) ?? 0), 0)
        : undefined

      const fallbackInventory =
        typeof product.inventory === 'number' && Number.isFinite(product.inventory) ? product.inventory : 0

      const createdAt = product.createdAt || (ids.length > 0 ? createdAtById.get(ids[0]) : undefined)
      const daysInInventory = calculateDaysInInventory(createdAt)
      const daysSinceLastSale = calculateDaysSinceLastSale(product.lastSoldAt)

      return {
        ...product,
        inventory: inventoryFromIds ?? fallbackInventory,
        createdAt,
        daysInInventory,
        daysSinceLastSale,
      }
    })
  }, [productsFromReport, inventoryById, createdAtById])

  const totalNetFromOrders = useMemo(() => {
    if (!report?.orders?.length) return 0
    return report.orders.reduce((sum, order) => {
      const net = (order as any).netSales
      if (typeof net === 'number' && Number.isFinite(net)) {
        return sum + net
      }
      const subtotal = parseMoney((order as any).subtotal_price)
      const discounts = parseMoney((order as any).total_discounts)
      return sum + (subtotal - discounts)
    }, 0)
  }, [report?.orders])

  const lastSoldByName = useMemo(() => {
    const map = new Map<string, string>()
    productsWithInventory.forEach((product) => {
      if (product.lastSoldAt) {
        map.set(product.productName, product.lastSoldAt)
      }
    })
    return map
  }, [productsWithInventory])

  const tableTotalNetSales = useMemo(() => {
    return productsWithInventory.reduce((sum, product) => sum + (product.totalSales ?? 0), 0)
  }, [productsWithInventory])

  const tableTotalUnits = useMemo(() => {
    return productsWithInventory.reduce((sum, product) => sum + (product.unitsSold ?? 0), 0)
  }, [productsWithInventory])

  const productCount = productsWithInventory.length ?? 0

  // Aplicar filtros a los productos con ventas
  const filteredProductsWithInventory = useMemo(() => {
    let filtered = productsWithInventory

    // Filtro de inventario
    if (inventoryFilter === 'with-inventory') {
      filtered = filtered.filter((p) => (p.inventory ?? 0) > 0)
    } else if (inventoryFilter === 'zero-inventory') {
      filtered = filtered.filter((p) => (p.inventory ?? 0) === 0)
    }

    // Nota: statusFilter no aplica aquí porque productsWithInventory viene del reporte
    // y no tiene campo status. Los filtros de status se aplican en lowRotation y zeroInventory

    if (brandFilter !== 'all') {
      filtered = filtered.filter((p) => {
        const ids = Array.isArray(p.productIds) ? p.productIds : []
        const vendor = ids.length > 0
          ? allProducts.find((a) => ids.includes(String(a.id)))?.vendor?.trim()
          : undefined
        return vendor === brandFilter
      })
    }

    if (lineFilter !== 'all') {
      filtered = filtered.filter((p) => {
        const ids = Array.isArray(p.productIds) ? p.productIds : []
        const pt = ids.length > 0
          ? allProducts.find((a) => ids.includes(String(a.id)))?.product_type?.trim()
          : undefined
        return pt === lineFilter
      })
    }

    return filtered
  }, [productsWithInventory, inventoryFilter, brandFilter, lineFilter, allProducts])

  const insightsProducts = useMemo(() => {
    if (!filteredProductsWithInventory.length) return []

    const products = [...filteredProductsWithInventory]

    switch (config.sort) {
      case 'most-sold':
        return products.sort((a, b) => (b.unitsSold ?? 0) - (a.unitsSold ?? 0))
      case 'less-sold':
        return products.sort((a, b) => (a.unitsSold ?? 0) - (b.unitsSold ?? 0))
      case 'highest-inventory':
        return products.sort((a, b) => (b.inventory ?? 0) - (a.inventory ?? 0))
      case 'lowest-inventory':
        return products.sort((a, b) => (a.inventory ?? 0) - (b.inventory ?? 0))
      case 'least-rotation':
        return products.sort((a, b) => {
          const aRatio = (a.unitsSold ?? 0) / Math.max(a.inventory ?? 1, 1)
          const bRatio = (b.unitsSold ?? 0) / Math.max(b.inventory ?? 1, 1)
          return aRatio - bRatio
        })
      case 'longest-in-inventory':
        return products.sort((a, b) => (b.daysInInventory ?? 0) - (a.daysInInventory ?? 0))
      case 'longest-without-sales':
        return products.sort((a, b) => (b.daysSinceLastSale ?? 0) - (a.daysSinceLastSale ?? 0))
      default:
        return products.sort((a, b) => (b.totalSales ?? 0) - (a.totalSales ?? 0))
    }
  }, [config.sort, filteredProductsWithInventory])

  const tableSortedProducts = useMemo(() => {
    return insightsProducts
  }, [insightsProducts])

  const starProducts = useMemo(() => {
    if (!insightsProducts.length) return []
    return [...insightsProducts].sort((a, b) => (b.totalSales ?? 0) - (a.totalSales ?? 0))
  }, [insightsProducts])

  const starPreviewProducts = starProducts.slice(0, 10)

  const lowRotationProducts = useMemo(() => {
    if (!allProducts.length) return []

    const soldProductNames = new Set(productsWithInventory.map((p) => p.productName))

    let filtered = allProducts
      .filter((product) => !soldProductNames.has(product.title))

    // Aplicar filtro de estado
    if (statusFilter === 'active') {
      filtered = filtered.filter((product) => product.status === 'active')
    } else if (statusFilter === 'archived') {
      filtered = filtered.filter((product) => product.status === 'archived')
    }

    if (brandFilter !== 'all') {
      filtered = filtered.filter((product) => product.vendor?.trim() === brandFilter)
    }

    if (lineFilter !== 'all') {
      filtered = filtered.filter((product) => product.product_type?.trim() === lineFilter)
    }

    const products = filtered
      .map((product) => {
        const id = String(product.id)
        const daysInInventory = calculateDaysInInventory(product.createdAt)

        return {
          id,
          title: product.title,
          inventory: Number(product.inventory ?? 0),
          lastSoldAt: lastSoldByName.get(product.title) ?? null,
          daysInInventory: daysInInventory,
          handle: product.handle,
        }
      })
      .filter((product) => product.inventory > 0)
      .filter((product) => (product.daysInInventory ?? 0) > 30)

    // Aplicar ordenamiento según configuración
    switch (config.sort) {
      case 'highest-inventory':
        return products.sort((a, b) => b.inventory - a.inventory)
      case 'lowest-inventory':
        return products.sort((a, b) => a.inventory - b.inventory)
      case 'longest-in-inventory':
        return products.sort((a, b) => (b.daysInInventory ?? 0) - (a.daysInInventory ?? 0))
      default:
        return products.sort((a, b) => (b.daysInInventory ?? 0) - (a.daysInInventory ?? 0))
    }
  }, [allProducts, productsWithInventory, lastSoldByName, config.sort, statusFilter, brandFilter, lineFilter])

  const lowRotationPreview = lowRotationProducts.slice(0, 10)

  const zeroInventoryProducts = useMemo(() => {
    if (!allProducts.length) return []

    const soldProductNames = new Set(productsWithInventory.map((p) => p.productName))

    let filtered = allProducts
      .filter((product) =>
        !soldProductNames.has(product.title) &&
        Number(product.inventory ?? 0) === 0
      )

    // Aplicar filtro de estado
    if (statusFilter === 'active') {
      filtered = filtered.filter((product) => product.status === 'active')
    } else if (statusFilter === 'archived') {
      filtered = filtered.filter((product) => product.status === 'archived')
    }

    if (brandFilter !== 'all') {
      filtered = filtered.filter((product) => product.vendor?.trim() === brandFilter)
    }

    if (lineFilter !== 'all') {
      filtered = filtered.filter((product) => product.product_type?.trim() === lineFilter)
    }

    const products = filtered
      .map((product) => {
        const id = String(product.id)
        const daysInInventory = calculateDaysInInventory(product.createdAt)

        return {
          id,
          title: product.title,
          inventory: 0,
          lastSoldAt: lastSoldByName.get(product.title) ?? null,
          daysInInventory: daysInInventory,
          handle: product.handle,
        }
      })

    // Aplicar ordenamiento según configuración
    switch (config.sort) {
      case 'longest-in-inventory':
        return products.sort((a, b) => (b.daysInInventory ?? 0) - (a.daysInInventory ?? 0))
      default:
        return products.sort((a, b) => (b.daysInInventory ?? 0) - (a.daysInInventory ?? 0))
    }
  }, [allProducts, productsWithInventory, lastSoldByName, config.sort, statusFilter, brandFilter, lineFilter])

  const zeroInventoryPreview = zeroInventoryProducts.slice(0, 10)

  useEffect(() => {
    const pending = new Set<string>()
    const enqueueId = (value?: string | null) => {
      const trimmed = value?.trim()
      if (!trimmed) return
      if (!productImages[trimmed]) {
        pending.add(trimmed)
      }
    }

    productsWithInventory.forEach((row) => {
      row.productIds?.forEach((id) => enqueueId(id))
    })

    lowRotationProducts.slice(0, 30).forEach((product) => enqueueId(product.id))
    zeroInventoryProducts.slice(0, 30).forEach((product) => enqueueId(product.id))

    if (pending.size === 0) return

    let cancelled = false
    const ids = Array.from(pending)

    FunctionsService.getProductsByIds(ids)
      .then((response) => {
        if (cancelled) return
        if (response.error) {
          toast({
            title: 'No se pudieron cargar las imágenes',
            description: response.error,
            variant: 'destructive',
          })
          return
        }

        if (response.data) {
          setProductImages((prev) => {
            const next = { ...prev }
            Object.entries(response.data!).forEach(([id, info]) => {
              next[id] = info ?? {}
            })
            return next
          })
        }
      })
      .catch((error: any) => {
        if (cancelled) return
        toast({
          title: 'Error al cargar imágenes',
          description: error?.message ?? 'No se pudieron obtener los datos del producto.',
          variant: 'destructive',
        })
      })

    return () => {
      cancelled = true
    }
  }, [productsWithInventory, lowRotationProducts, zeroInventoryProducts, productImages, toast])
  const currentFrom = dateRange.from ?? DEFAULT_RANGE.from
  const currentTo = dateRange.to ?? DEFAULT_RANGE.to

  const summaryCards = [
    {
      title: 'Ventas netas (período)',
      value: formatCurrency(totalNetFromOrders),
      icon: DollarSign,
    },
    {
      title: 'Unidades vendidas',
      value: tableTotalUnits.toLocaleString('es-ES'),
      icon: Package,
    },
    {
      title: 'Ticket promedio',
      value: formatCurrency(tableTotalUnits > 0 ? totalNetFromOrders / tableTotalUnits : 0),
      icon: Percent,
    },
    {
      title: 'Productos analizados',
      value: productCount.toString(),
      icon: ShoppingBag,
    },
  ]

  const resolveProductImage = (product: { imageUrl?: string | null; productIds?: string[] }) => {
    const fallback =
      product.productIds
        ?.map((id) => productImages[id]?.imageUrl)
        .find((value) => typeof value === 'string' && value.length > 0) ?? null

    return product.imageUrl ?? fallback ?? undefined
  }

  const renderProductRow = (product: ProductSale & { productIds?: string[] }) => {
    const units = product.unitsSold ?? 0
    const totalSales = product.totalSales ?? 0
    const percentage = tableTotalNetSales > 0 ? (totalSales / tableTotalNetSales) * 100 : 0

    const imageUrl = resolveProductImage(product)
    const imageSrc = imageUrl ?? '/placeholder.svg'
    const canPreview = Boolean(imageUrl)

    const inventoryValue =
      typeof product.inventory === 'number' && Number.isFinite(product.inventory) ? product.inventory : 0
    const normalizedInventory = Math.max(0, Math.round(inventoryValue))

    return (
      <TableRow key={product.productName} className="border-white/10">
        <TableCell>
          <div className="flex items-center gap-3">
            {canPreview ? (
              <button
                type="button"
                onClick={() => setPreviewProduct({ src: imageUrl!, title: product.productName })}
                className="group relative h-10 w-10 overflow-hidden rounded-md bg-gray-800 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                aria-label={`Ver imagen de ${product.productName}`}
              >
                <img src={imageSrc} alt={product.productName} className="h-full w-full object-cover" loading="lazy" />
                <span className="absolute inset-0 bg-black/30 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100" />
              </button>
            ) : (
              <div className="h-10 w-10 overflow-hidden rounded-md bg-gray-800">
                <img src={imageSrc} alt={product.productName} className="h-full w-full object-cover" loading="lazy" />
              </div>
            )}
            <span className="font-medium text-white">{product.productName}</span>
          </div>
        </TableCell>
        <TableCell className="text-right text-white/90">{units.toLocaleString('es-ES')}</TableCell>
        <TableCell className="text-right text-white/90">{formatCurrency(totalSales)}</TableCell>
        <TableCell className="text-right text-white/90">{formatPercent(percentage)}</TableCell>
        <TableCell className="text-right">
          <span
            className={cn(
              'font-semibold',
              normalizedInventory === 0 && 'text-white/60',
              normalizedInventory > 0 && normalizedInventory <= 5 && 'text-red-400',
              normalizedInventory > 5 && normalizedInventory <= 10 && 'text-amber-300',
              normalizedInventory > 10 && 'text-emerald-400'
            )}
          >
            {normalizedInventory.toLocaleString('es-ES')}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <span className="text-white/70">
            {product.daysInInventory !== undefined ? `${product.daysInInventory}` : '-'}
          </span>
        </TableCell>
        <TableCell className="text-right">
          <span className={cn(
            'font-medium',
            product.daysSinceLastSale === undefined && 'text-white/40',
            product.daysSinceLastSale !== undefined && product.daysSinceLastSale > 90 && 'text-red-400',
            product.daysSinceLastSale !== undefined && product.daysSinceLastSale > 60 && product.daysSinceLastSale <= 90 && 'text-amber-400',
            product.daysSinceLastSale !== undefined && product.daysSinceLastSale > 30 && product.daysSinceLastSale <= 60 && 'text-yellow-400',
            product.daysSinceLastSale !== undefined && product.daysSinceLastSale <= 30 && 'text-emerald-400'
          )}>
            {product.daysSinceLastSale !== undefined ? `${product.daysSinceLastSale}` : '-'}
          </span>
        </TableCell>
      </TableRow>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
    <div className="mx-auto w-full max-w-[1600px] px-4 py-8 sm:px-6 lg:px-10">
      <Header />

      <Card className="mb-8 border-white/10 bg-white/5 backdrop-blur-xl">
        <CardContent className="space-y-4 p-6">
          <div className="flex items-center gap-3 overflow-x-auto no-scrollbar pb-1">
            <Button
              variant="outline"
              className={cn(
                'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                selectedPreset === 'today' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
              )}
              onClick={() => handlePresetChange('today')}
            >
              Hoy
            </Button>
            <Button
              variant="outline"
              className={cn(
                'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                selectedPreset === 'month-to-date' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
              )}
              onClick={() => handlePresetChange('month-to-date')}
            >
              Días del mes
            </Button>
            <Button
              variant="outline"
              className={cn(
                'flex-shrink-0 bg-white/10 text-white hover:bg-white/20',
                selectedPreset === 'full-month' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
              )}
              onClick={() => handlePresetChange('full-month')}
            >
              Mes completo
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'flex-shrink-0 justify-start bg-white/10 text-left font-normal text-white hover:bg-white/20 md:w-[280px]',
                    selectedPreset === 'custom' && 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {currentFrom && currentTo ? `${format(currentFrom, 'P', { locale: es })} - ${format(currentTo, 'P', { locale: es })}` : ''}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto bg-gray-900 p-0" align="start">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  locale={es}
                  selected={dateRange}
                  defaultMonth={currentFrom}
                  onSelect={handleDateChange}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex overflow-x-auto no-scrollbar gap-2 pb-1 items-center">
            <Button
              className="flex-shrink-0 flex items-center gap-2 bg-emerald-500 text-white hover:bg-emerald-500/90"
              onClick={handleGenerateClick}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Generar
            </Button>
            <Button
              variant="outline"
              className="flex-shrink-0 flex items-center gap-2 bg-red-500/20 text-red-200 hover:bg-red-500/30"
              onClick={handleClearCache}
            >
              <Trash2 className="h-4 w-4" />
              Limpiar caché
            </Button>
            <Button
              variant="outline"
              className="flex-shrink-0 flex items-center gap-2 bg-blue-500/20 text-blue-200 hover:bg-blue-500/30"
              onClick={() => setIsDebugOpen(true)}
            >
              <FileJson className="h-4 w-4" />
              Debug
            </Button>
            <div className="flex-shrink-0 flex items-center gap-2 rounded-md bg-white/5 px-3 py-2">
              <Switch
                id="force-shopify-products"
                checked={forceShopify}
                onCheckedChange={setForceShopify}
                className="data-[state=checked]:bg-blue-500"
              />
              <Label htmlFor="force-shopify-products" className="cursor-pointer text-sm text-white">
                Forzar datos de Shopify {forceShopify && '(habilitado)'}
              </Label>
            </div>
            <SalesConfigDialog config={config} onSave={setConfig} />
          </div>
        </CardContent>
      </Card>

      {/* Filtros */}
      <div className="mt-3 space-y-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3 backdrop-blur-xl">
        {/* Brand filter */}
        {brands.length > 1 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-white/40">Marca</span>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
              <button
                onClick={() => setBrandFilter('all')}
                className={cn(
                  'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                  brandFilter === 'all'
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                )}
              >
                Todas
              </button>
              {brands.map((brand) => (
                <button
                  key={brand}
                  onClick={() => setBrandFilter(brand === brandFilter ? 'all' : brand)}
                  className={cn(
                    'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                    brandFilter === brand
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  )}
                >
                  {brand}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Product type / Line filter */}
        {productTypes.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-white/40">Línea de producto</span>
            <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
              <button
                onClick={() => setLineFilter('all')}
                className={cn(
                  'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                  lineFilter === 'all'
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                )}
              >
                Todas
              </button>
              {productTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setLineFilter(type === lineFilter ? 'all' : type)}
                  className={cn(
                    'flex-shrink-0 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                    lineFilter === type
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  )}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-white/40">Estado</span>
            <div className="flex gap-1.5">
              {([
                { key: 'all', label: 'Todos', active: 'border-blue-500 bg-blue-500 text-white' },
                { key: 'active', label: 'Activos', active: 'border-emerald-500 bg-emerald-500 text-white' },
                { key: 'archived', label: 'Archivados', active: 'border-red-500 bg-red-500 text-white' },
              ] as const).map(({ key, label, active }) => (
                <button
                  key={key}
                  onClick={() => setStatusFilter(key)}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                    statusFilter === key ? active : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-white/40">Inventario</span>
            <div className="flex gap-1.5">
              {([
                { key: 'all', label: 'Todos', active: 'border-blue-500 bg-blue-500 text-white' },
                { key: 'with-inventory', label: 'Con stock', active: 'border-emerald-500 bg-emerald-500 text-white' },
                { key: 'zero-inventory', label: 'Sin stock', active: 'border-amber-500 bg-amber-500 text-white' },
              ] as const).map(({ key, label, active }) => (
                <button
                  key={key}
                  onClick={() => setInventoryFilter(key)}
                  className={cn(
                    'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors',
                    inventoryFilter === key ? active : 'border-white/10 bg-white/10 text-white/70 hover:bg-white/20 hover:text-white'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {(statusFilter !== 'all' || inventoryFilter !== 'all' || brandFilter !== 'all' || lineFilter !== 'all') && (
            <button
              onClick={() => { setStatusFilter('all'); setInventoryFilter('all'); setBrandFilter('all'); setLineFilter('all') }}
              className="self-end text-xs text-white/40 hover:text-white transition-colors"
            >
              Limpiar ✕
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="my-4 rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {summaryCards.map((card) => (
          <Card key={card.title} className="border-white/10 bg-white/5 text-white backdrop-blur">
            <CardHeader className="pb-1 px-4 pt-4 flex flex-row items-center justify-between space-y-0">
              <CardDescription className="text-xs text-white/60">{card.title}</CardDescription>
              <card.icon className="h-3.5 w-3.5 text-white/30 flex-shrink-0" />
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <p className="text-xl font-bold text-white tabular-nums">{card.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <section className={cn('mt-4 grid gap-4', zeroInventoryProducts.length === 0 ? 'lg:grid-cols-2' : 'lg:grid-cols-3')}>
        <Card className="border-white/10 bg-white/5 text-white backdrop-blur-xl overflow-hidden">
          <CardHeader className="py-4 px-5">
            <CardTitle className="text-sm font-semibold text-white">Productos estrella</CardTitle>
            <CardDescription className="text-xs text-white/50">
              {tableTotalNetSales > 0 ? 'Top 10 mayor contribución en el período.' : 'Ordenados por configuración actual.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {starPreviewProducts.length > 0 ? (
              <>
                <ul className="space-y-3">
                  {starPreviewProducts.map((product) => {
                    const imageUrl = resolveProductImage(product)
                    const canPreview = Boolean(imageUrl)
                    const imageSrc = imageUrl ?? '/placeholder.svg'
                    const hasInventory = typeof product.inventory === 'number' && product.inventory > 0

                    return (
                      <li key={product.productName} className="flex items-center gap-4">
                        {canPreview ? (
                          <button
                            type="button"
                            onClick={() => setPreviewProduct({ src: imageUrl!, title: product.productName })}
                            className="group relative h-12 w-12 overflow-hidden rounded-full bg-emerald-500/15 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            aria-label={`Ver imagen de ${product.productName}`}
                          >
                            <img src={imageSrc} alt={product.productName} className="h-full w-full object-cover" />
                            <span className="absolute inset-0 bg-emerald-500/20 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100" />
                          </button>
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-300">
                            <TrendingUp className="h-6 w-6" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="font-semibold text-white">{product.productName}</p>
                          <p className="text-sm text-white/60">
                            {product.totalSales > 0 ? (
                              <>
                                {formatCurrency(product.totalSales)}
                                {' - '}
                                {product.unitsSold?.toLocaleString('es-ES') ?? 0} unidades
                              </>
                            ) : (
                              <>
                                {hasInventory ? `Inventario: ${product.inventory}` : 'Sin ventas en este período'}
                              </>
                            )}
                          </p>
                          {(product.daysInInventory !== undefined || product.daysSinceLastSale !== undefined) && (
                            <p className="text-xs text-white/40">
                              {product.daysInInventory !== undefined && `${product.daysInInventory} días en tienda`}
                              {product.daysInInventory !== undefined && product.daysSinceLastSale !== undefined && ' • '}
                              {product.daysSinceLastSale !== undefined && `${product.daysSinceLastSale} días sin venta`}
                            </p>
                          )}
                        </div>
                      </li>
                    )
                  })}
                </ul>
                {starProducts.length > 10 && (
                  <Button
                    variant="outline"
                    className="mt-3 w-full bg-white/10 text-white hover:bg-white/20 border-white/10 text-sm h-8"
                    onClick={() =>
                      navigate('/productos/estrella', {
                        state: {
                          products: starProducts,
                          totalNetSales: tableTotalNetSales,
                          dateLabel:
                            currentFrom && currentTo
                              ? `${format(currentFrom, "d 'de' MMM", { locale: es })} – ${format(currentTo, "d 'de' MMM yyyy", { locale: es })}`
                              : '',
                          productImages,
                        },
                      })
                    }
                  >
                    Ver todos ({starProducts.length})
                  </Button>
                )}
              </>
            ) : (
              <p className="text-sm text-white/60">No hay datos para este rango.</p>
            )}
          </CardContent>
        </Card>

        <Card className="border-white/10 bg-white/5 text-white backdrop-blur-xl overflow-hidden">
          <CardHeader className="py-4 px-5">
            <CardTitle className="text-sm font-semibold text-white">Menor rotación</CardTitle>
            <CardDescription className="text-xs text-white/50">Sin ventas, +30 días en tienda.</CardDescription>
          </CardHeader>
          <CardContent>
            {lowRotationPreview.length > 0 ? (
              <>
                <ul className="space-y-3">
                  {lowRotationPreview.map((product) => {
                    // Buscar el producto completo en allProducts
                    const fullProduct = allProducts.find(p => String(p.id) === product.id)
                    const productInfo = productImages[product.id]
                    const imageUrl = fullProduct?.imageUrl ?? productInfo?.imageUrl ?? null
                    const canPreview = Boolean(imageUrl)
                    const imageSrc = imageUrl ?? '/placeholder.svg'
                    const daysInInventory = product.daysInInventory
                    const handle = fullProduct?.handle ?? productInfo?.handle ?? product.handle
                    const shopifyUrl = handle ? `https://f3295a-3.myshopify.com/products/${handle}` : null

                    return (
                      <li key={product.id} className="flex items-center gap-4">
                        {canPreview ? (
                          <button
                            type="button"
                            onClick={() => setPreviewProduct({ src: imageUrl!, title: product.title })}
                            className="group relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full bg-amber-500/15 focus:outline-none focus:ring-2 focus:ring-amber-400"
                            aria-label={`Ver imagen de ${product.title}`}
                          >
                            <img src={imageSrc} alt={product.title} className="h-full w-full object-cover" />
                            <span className="absolute inset-0 bg-amber-500/20 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100" />
                          </button>
                        ) : (
                          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/20 text-amber-300">
                            <TrendingDown className="h-6 w-6" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white truncate">{product.title}</p>
                          <p className="text-sm text-white/60">Inventario: {product.inventory}</p>
                          <p className="text-xs text-white/40">
                            {daysInInventory !== undefined && `${daysInInventory} días en tienda`}
                          </p>
                        </div>
                        {shopifyUrl && (
                          <a
                            href={shopifyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 rounded-md bg-white/10 px-3 py-1.5 text-xs text-white transition hover:bg-white/20"
                          >
                            Ver en Shopify
                          </a>
                        )}
                      </li>
                    )
                  })}
                </ul>
                {lowRotationProducts.length > 10 && (
                  <Button
                    variant="outline"
                    className="mt-3 w-full bg-white/10 text-white hover:bg-white/20 border-white/10 text-sm h-8"
                    onClick={() => navigate('/productos/menor-rotacion')}
                  >
                    Ver todos ({lowRotationProducts.length})
                  </Button>
                )}
              </>
            ) : (
              <p className="text-sm text-white/60">No hay productos con baja rotacion en este periodo.</p>
            )}
          </CardContent>
        </Card>
        
        {zeroInventoryProducts.length > 0 && (
        <Card className="border-white/10 bg-white/5 text-white backdrop-blur-xl overflow-hidden">
          <CardHeader className="py-4 px-5">
            <CardTitle className="text-sm font-semibold text-white">Inventario Cero</CardTitle>
            <CardDescription className="text-xs text-white/50">Productos agotados sin ventas en el período.</CardDescription>
          </CardHeader>
          <CardContent>
            {zeroInventoryPreview.length > 0 ? (
              <>
                <ul className="space-y-3">
                  {zeroInventoryPreview.map((product) => {
                    // Buscar el producto completo en allProducts
                    const fullProduct = allProducts.find(p => String(p.id) === product.id)
                    const productInfo = productImages[product.id]
                    const imageUrl = fullProduct?.imageUrl ?? productInfo?.imageUrl ?? null
                    const canPreview = Boolean(imageUrl)
                    const imageSrc = imageUrl ?? '/placeholder.svg'
                    const daysInInventory = product.daysInInventory
                    const handle = fullProduct?.handle ?? productInfo?.handle ?? product.handle
                    const shopifyUrl = handle ? `https://f3295a-3.myshopify.com/products/${handle}` : null

                    return (
                      <li key={product.id} className="flex items-center gap-4">
                        {canPreview ? (
                          <button
                            type="button"
                            onClick={() => setPreviewProduct({ src: imageUrl!, title: product.title })}
                            className="group relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-full bg-red-500/15 focus:outline-none focus:ring-2 focus:ring-red-400"
                            aria-label={`Ver imagen de ${product.title}`}
                          >
                            <img src={imageSrc} alt={product.title} className="h-full w-full object-cover" />
                            <span className="absolute inset-0 bg-red-500/20 opacity-0 transition group-hover:opacity-100 group-focus-visible:opacity-100" />
                          </button>
                        ) : (
                          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-500/20 text-red-300">
                            <TrendingDown className="h-6 w-6" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-white truncate">{product.title}</p>
                          <p className="text-xs text-white/40">
                            {daysInInventory !== undefined && `${daysInInventory} días desde creación`}
                          </p>
                        </div>
                        {shopifyUrl && (
                          <a
                            href={shopifyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 rounded-md bg-white/10 px-3 py-1.5 text-xs text-white transition hover:bg-white/20"
                          >
                            Ver en Shopify
                          </a>
                        )}
                      </li>
                    )
                  })}
                </ul>
                {zeroInventoryProducts.length > 10 && (
                  <p className="mt-3 text-center text-xs text-white/40">
                    y {zeroInventoryProducts.length - 10} más sin inventario
                  </p>
                )}
              </>
            ) : null}
          </CardContent>
        </Card>
        )}
      </section>

      <Card className="mt-8 border-white/10 bg-white/5 text-white backdrop-blur-xl">
        <CardHeader>
          <CardTitle>Rendimiento por producto</CardTitle>
          <CardDescription>
            Comparativo de ventas, porcentaje de aporte e inventario actual por producto.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-[400px] w-full bg-white/10" />
          ) : !tableSortedProducts.length ? (
            <div className="rounded-lg border border-dashed border-white/15 p-10 text-center text-white/60">
              No hay datos disponibles para el rango seleccionado.
            </div>
          ) : (
            <div>
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-white/60">
                  Orden actual: <span className="font-semibold text-white">{sortOptionLabels[config.sort]}</span>
                </p>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10">
                      <TableHead className="text-white/60">Producto</TableHead>
                      <TableHead className="text-right text-white/60">Unidades</TableHead>
                      <TableHead className="text-right text-white/60">Ventas netas</TableHead>
                      <TableHead className="text-right text-white/60">% del total</TableHead>
                      <TableHead className="text-right text-white/60">Inventario</TableHead>
                      <TableHead className="text-right text-white/60">Días en tienda</TableHead>
                      <TableHead className="text-right text-white/60">Días sin venta</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>{tableSortedProducts.map((product) => renderProductRow(product))}</TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(previewProduct)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewProduct(null)
          }
        }}
      >
        <DialogContent className="max-w-3xl border-white/20 bg-gray-900/95 text-white backdrop-blur-xl">
          <DialogClose asChild>
            <button
              type="button"
              className="absolute right-4 top-4 text-sm text-white/70 transition hover:text-white"
            >
              Cerrar
            </button>
          </DialogClose>
          <DialogHeader>
            <DialogTitle>{previewProduct?.title ?? 'Vista de producto'}</DialogTitle>
            <DialogDescription className="text-white/60">
              Imagen ampliada del producto seleccionado.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center">
            {previewProduct?.src ? (
              <img
                src={previewProduct.src}
                alt={previewProduct.title}
                className="max-h-[70vh] w-auto rounded-lg border border-white/10 object-contain shadow-lg"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDebugOpen} onOpenChange={setIsDebugOpen}>
        <DialogContent className="max-w-4xl h-[80vh] bg-gray-900/95 border-white/20 text-white backdrop-blur-xl overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-2xl">Datos del Reporte (JSON)</DialogTitle>
            <DialogDescription className="text-white/60">
              JSON completo del reporte de productos.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-auto rounded-lg bg-black/50 p-4">
            <pre className="text-xs text-green-300 whitespace-pre-wrap">
              {JSON.stringify(report, null, 2)}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </div>
  )
}

interface SalesConfigDialogProps {
  config: SalesConfig
  onSave: (config: SalesConfig) => void
}

function SalesConfigDialog({ config, onSave }: SalesConfigDialogProps) {
  const [currentConfig, setCurrentConfig] = useState(config)

  useEffect(() => {
    setCurrentConfig(config)
  }, [config])

  const handleSave = () => {
    onSave(currentConfig)
  }

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="bg-white/10 text-white hover:bg-white/20">
          <Settings className="mr-2 h-4 w-4" />
          Configurar ventas
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md border-white/20 bg-gray-900/90 text-white backdrop-blur-xl">
        <DialogHeader>
          <DialogTitle>Configuración de ventas</DialogTitle>
          <DialogDescription className="text-white/60">
            Ajusta el orden y los filtros para analizar el rendimiento de productos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label>Ordenar por</Label>
            <Select
              value={currentConfig.sort}
              onValueChange={(value: SortOption) => setCurrentConfig((prev) => ({ ...prev, sort: value }))}
            >
              <SelectTrigger className="bg-gray-800 border-white/20">
                <SelectValue placeholder="Selecciona orden" />
              </SelectTrigger>
              <SelectContent className="bg-gray-800 border-white/20">
                <SelectItem value="most-sold">Más vendidos</SelectItem>
                <SelectItem value="less-sold">Menos vendidos</SelectItem>
                <SelectItem value="highest-inventory">Mayor inventario</SelectItem>
                <SelectItem value="lowest-inventory">Menor inventario</SelectItem>
                <SelectItem value="least-rotation">Menor rotación</SelectItem>
                <SelectItem value="longest-in-inventory">Más días en inventario</SelectItem>
                <SelectItem value="longest-without-sales">Más días sin venderse</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogClose asChild>
          <Button onClick={handleSave} className="mt-4 w-full bg-emerald-500 text-white hover:bg-emerald-500/90">
            <TrendingUp className="mr-2 h-4 w-4" />
            Aplicar
          </Button>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}

export default ProductSalesDashboard
